import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import notificationStore from '../../stores/notificationStore';
import {
  NotificationResponseRecovery,
  applyNotificationOutcome,
  submitNotificationResponse,
} from './notificationResponseClient';

/**
 * Severity configuration — colors, icons, and behavior rules.
 * Emergency = critical severity in the existing system.
 */
const SEVERITY_CONFIG = {
  critical: {
    label: 'Emergency',
    icon: ReportProblemIcon,
    canDismiss: false,
    borderColor: '#d32f2f',
    accentBg: 'rgba(211, 47, 47, 0.06)',
    iconColor: '#d32f2f',
    chipColor: 'error',
  },
  error: {
    label: 'High',
    icon: ErrorOutlineIcon,
    canDismiss: true,
    borderColor: '#ed6c02',
    accentBg: 'rgba(237, 108, 2, 0.06)',
    iconColor: '#ed6c02',
    chipColor: 'warning',
  },
  warning: {
    label: 'High',
    icon: WarningAmberIcon,
    canDismiss: true,
    borderColor: '#ed6c02',
    accentBg: 'rgba(237, 108, 2, 0.06)',
    iconColor: '#ed6c02',
    chipColor: 'warning',
  },
  info: {
    label: 'Normal',
    icon: InfoOutlinedIcon,
    canDismiss: true,
    borderColor: '#1976d2',
    accentBg: 'rgba(25, 118, 210, 0.04)',
    iconColor: '#1976d2',
    chipColor: 'info',
  },
};

function getSeverityConfig(severity) {
  return SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;
}

/**
 * Format timestamp as short date/time in user's local timezone
 * e.g. "2/11/26, 9:30 AM"
 */
function formatShortDateTime(timestamp) {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    return d.toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
}

/**
 * NotificationResponseModal
 *
 * A modern, persistent, decision-first modal/card for actionable notifications.
 * Renders up to 2 response buttons. Severity-driven styling.
 * Emergency (critical) cannot be dismissed until responded to.
 *
 * Every card is fully visible and answerable; several waiting at once are laid out
 * as a list by NotificationResponseStack rather than overlapping.
 *
 * Props:
 *   notification  - notification object from notificationStore
 *   onDismiss     - called when user dismisses (if allowed)
 */
const NotificationResponseModal = ({ notification, onDismiss }) => {
  const theme = useTheme();
  const [responding, setResponding] = useState(false);

  const config = getSeverityConfig(notification?.severity);
  const SeverityIcon = config.icon;
  const isDark = theme.palette.mode === 'dark';

  const handleResponseClick = useCallback(async (responseKey) => {
    if (!notification?.actionId || responding) return;

    setResponding(true);
    notificationStore.markAsResponded(notification.id, 'pending');
    const outcome = await submitNotificationResponse({ notification, responseKey });
    applyNotificationOutcome(notification.id, outcome);
    setResponding(false);
  }, [notification, responding]);

  const handleDismiss = useCallback(() => {
    if (!config.canDismiss && notification?.responseStatus !== 'accepted' && notification?.responseStatus !== 'rejected') {
      return;
    }
    if (onDismiss) onDismiss(notification.id);
  }, [notification, config.canDismiss, onDismiss]);

  if (!notification) return null;

  const isHandled = notification.responseStatus === 'accepted' || notification.responseStatus === 'rejected';
  const isPending = notification.responseStatus === 'pending';
  const isError = notification.responseStatus === 'error';
  const canDismissNow = config.canDismiss || isHandled;

  // No subtitle — the title is sufficient
  const subtitle = null;

  // Build footer text
  const footerText = formatShortDateTime(notification.timestamp);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        width: { xs: 'min(320px, calc(100vw - 24px))', sm: 320 },
        maxWidth: 320,
        borderRadius: '12px',
        overflow: 'visible',
        border: isDark ? '1.5px solid #64748b' : '1.5px solid #334155',
        outline: 'none',
        boxShadow: isDark
          ? '0 10px 28px rgba(0, 0, 0, 0.55)'
          : '0 10px 28px rgba(15, 23, 42, 0.22)',
        bgcolor: isDark ? 'grey.800' : '#f8fafc',
        pointerEvents: 'auto',
      }}
      data-testid="notification-response-modal"
      data-notification-id={notification.id}
      data-severity={notification.severity}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.75,
          pt: 1.5,
          pb: 0.75,
        }}
      >
        <Box
          sx={{
            mt: 0.15,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: config.accentBg,
            flexShrink: 0,
          }}
        >
          <SeverityIcon sx={{ fontSize: 18, color: config.iconColor }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: '0.9rem',
              lineHeight: 1.25,
              color: isDark ? 'grey.100' : 'grey.900',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {notification.title || 'Notification'}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'grey.500' : 'grey.600',
                fontSize: '0.75rem',
                lineHeight: 1.3,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {canDismissNow && (
          <IconButton
            size="small"
            onClick={handleDismiss}
            disableRipple
            sx={{
              width: 28,
              height: 28,
              minWidth: 28,
              p: 0,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: '#dc2626',
              color: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.28)',
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
              '&.MuiIconButton-root': {
                backgroundColor: '#dc2626',
              },
              '&.MuiIconButton-root:hover': {
                backgroundColor: '#b91c1c',
              },
            }}
            aria-label="dismiss notification"
          >
            <CloseIcon sx={{ fontSize: 18, color: '#fff' }} />
          </IconButton>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.75, py: 0.75 }}>
        <Typography
          variant="body2"
          sx={{
            color: isDark ? 'grey.300' : 'grey.800',
            fontSize: '0.8rem',
            lineHeight: 1.4,
          }}
        >
          {notification.message || 'Please confirm if you can respond.'}
        </Typography>
      </Box>

      {/* Actions / Post-click state */}
      <Box sx={{ px: 1.75, pb: 1.5, pt: 0.25 }}>
        {isHandled ? (
          /* Confirmation state */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1.5,
              px: 2,
              borderRadius: '12px',
              bgcolor: notification.responseStatus === 'accepted'
                ? (isDark ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.08)')
                : (isDark ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.04)'),
            }}
          >
            {notification.responseStatus === 'accepted' ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.main' }} />
            ) : (
              <InfoOutlinedIcon sx={{ fontSize: 20, color: isDark ? 'grey.400' : 'grey.600' }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: notification.responseStatus === 'accepted'
                  ? 'success.main'
                  : (isDark ? 'grey.400' : 'grey.600'),
              }}
            >
              {notification.responseMessage || (notification.responseStatus === 'accepted' ? 'Response sent' : 'Already handled')}
            </Typography>
          </Box>
        ) : (
          /* Response buttons */
          <>
            {isError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  label={notification.responseMessage || 'Error — try again'}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
                <NotificationResponseRecovery
                  notification={notification}
                  disabled={isPending}
                  onRetry={handleResponseClick}
                />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: 'row',
              }}
            >
              {(notification.responses || []).map((resp, idx) => (
                <Button
                  key={resp.responseKey}
                  variant={idx === 0 ? 'contained' : 'outlined'}
                  size="small"
                  fullWidth
                  disabled={isPending || !notification.actionId}
                  onClick={() => handleResponseClick(resp.responseKey)}
                  startIcon={
                    isPending && responding
                      ? <CircularProgress size={14} color="inherit" />
                      : undefined
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    borderRadius: '999px',
                    py: 0.6,
                    minHeight: 32,
                    border: idx === 0 ? 'none' : '1px solid rgba(15, 23, 42, 0.12)',
                    ...(idx === 0 ? {
                      bgcolor: config.borderColor,
                      '&:hover': { bgcolor: config.borderColor, filter: 'brightness(0.9)' },
                    } : {
                      bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
                    }),
                  }}
                >
                  {resp.responseLabel || resp.responseKey}
                </Button>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      {footerText && (
        <Box
          sx={{
            px: 1.75,
            pb: 1.25,
            pt: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isDark ? 'grey.600' : 'grey.500',
              fontSize: '0.75rem',
            }}
          >
            {footerText}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default NotificationResponseModal;
